import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ACCOUNT_TYPE_LABEL } from '../../lib/accountTypeLabel';
import { listAccounts } from '../../modules/accounts/api';
import type { Account } from '../../types/account';

// Multi-share Campaign visibility picker (Pre-P2b-3 §5.3). Renders the
// hierarchy under a root Account as an indented tree of checkboxes;
// AM ticks one or more nodes to set Campaign.shareTargets. No
// auto-cascade — checking a brand doesn't imply checking every
// descendant. The selection set is exactly what the AM picked.
//
// Used by:
//   - CampaignForm Visibility section (create + edit)
//   - "Save Alternate as Campaign" modal in the row editor
//
// Loads its own account hierarchy via React Query — callers don't have
// to wire data through. Search is debounced via internal state and
// filters by case-insensitive name match. When a leaf matches, every
// ancestor up to the root is also rendered (so the leaf's parent chain
// stays visible). Non-matching subtrees collapse out of view.

export type AccountTreePickerProps = {
  // Top of the tree to display. Only this Account and its descendants
  // appear; siblings and ancestors are not reachable.
  rootAccountId: string;
  // Currently-selected accountIds. Order is not significant.
  value: string[];
  onChange: (next: string[]) => void;
  // Optional list of accountIds the user can't select (rendered with the
  // checkbox disabled). Useful for "you can't deselect the root from this
  // surface" cases — none of the v1 surfaces use this, but the prop is
  // here for the Campaign editor where shareTargets validation could
  // disable the last remaining target.
  disabled?: string[];
};

type TreeNode = {
  account: Account;
  depth: number;
  children: TreeNode[];
};

function buildTree(rootId: string, accounts: Account[]): TreeNode | null {
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const root = byId.get(rootId);
  if (!root) return null;
  const childrenByParent = new Map<string, Account[]>();
  for (const a of accounts) {
    if (!a.parentAccountId) continue;
    const bucket = childrenByParent.get(a.parentAccountId) ?? [];
    bucket.push(a);
    childrenByParent.set(a.parentAccountId, bucket);
  }
  // Sort children alphabetically within each parent so the tree reads
  // predictably regardless of Firestore insertion order.
  for (const bucket of childrenByParent.values()) {
    bucket.sort((a, b) => a.name.localeCompare(b.name));
  }
  const build = (acc: Account, depth: number): TreeNode => ({
    account: acc,
    depth,
    children: (childrenByParent.get(acc.id) ?? []).map((c) => build(c, depth + 1)),
  });
  return build(root, 0);
}

// Returns the set of accountIds whose subtree contains a name-match for
// the query (including ancestors of matching leaves, which need to stay
// visible so the user can see WHERE the match sits in the hierarchy).
// Empty query returns null (caller renders the full tree).
function visibleIdsForQuery(tree: TreeNode, query: string): Set<string> | null {
  const q = query.trim().toLowerCase();
  if (q === '') return null;
  const visible = new Set<string>();
  const walk = (node: TreeNode): boolean => {
    const selfMatches = node.account.name.toLowerCase().includes(q);
    let anyChildMatches = false;
    for (const child of node.children) {
      if (walk(child)) anyChildMatches = true;
    }
    if (selfMatches || anyChildMatches) {
      visible.add(node.account.id);
      return true;
    }
    return false;
  };
  walk(tree);
  return visible;
}

// Flatten a tree to a depth-first list, optionally filtered by a
// visibility set. The caller renders one row per entry.
function flatten(
  tree: TreeNode,
  visible: Set<string> | null,
): TreeNode[] {
  const out: TreeNode[] = [];
  const walk = (node: TreeNode) => {
    if (visible !== null && !visible.has(node.account.id)) return;
    out.push(node);
    for (const child of node.children) walk(child);
  };
  walk(tree);
  return out;
}

export function AccountTreePicker({
  rootAccountId,
  value,
  onChange,
  disabled,
}: AccountTreePickerProps) {
  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: listAccounts,
  });

  const [searchInput, setSearchInput] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(searchInput), 200);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const tree = useMemo(
    () =>
      accountsQuery.data ? buildTree(rootAccountId, accountsQuery.data) : null,
    [accountsQuery.data, rootAccountId],
  );

  const visible = useMemo(
    () => (tree ? visibleIdsForQuery(tree, debounced) : null),
    [tree, debounced],
  );

  const flat = useMemo(() => (tree ? flatten(tree, visible) : []), [tree, visible]);

  const selected = useMemo(() => new Set(value), [value]);
  const disabledSet = useMemo(() => new Set(disabled ?? []), [disabled]);

  const toggle = (id: string) => {
    if (disabledSet.has(id)) return;
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };

  const clearAll = () => onChange([]);

  if (accountsQuery.isLoading) {
    return <p className="text-sm text-slate-500">Loading accounts…</p>;
  }
  if (accountsQuery.error || !tree) {
    return (
      <p className="text-sm text-red-600">
        Failed to load account hierarchy
        {accountsQuery.error
          ? `: ${(accountsQuery.error as Error).message}`
          : ' (root not found).'}
      </p>
    );
  }

  return (
    <div className="space-y-2 rounded-md border border-slate-300 bg-white p-3">
      <div className="flex items-center justify-between gap-3">
        <input
          type="search"
          className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
          placeholder="Filter by name…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <span className="text-xs text-slate-500 whitespace-nowrap">
          Selected: {value.length}
        </span>
        {value.length > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="text-xs text-slate-500 hover:text-slate-900 underline"
          >
            Clear all
          </button>
        )}
      </div>
      <ul className="max-h-80 overflow-y-auto pr-1 text-sm">
        {flat.length === 0 ? (
          <li className="text-slate-400 italic px-2 py-1">No accounts match.</li>
        ) : (
          flat.map((node) => {
            const id = node.account.id;
            const isDisabled = disabledSet.has(id);
            return (
              <li key={id} className="py-0.5">
                <label
                  className={`flex items-center gap-2 rounded px-1 hover:bg-slate-50 ${
                    isDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                  }`}
                  style={{ paddingLeft: `${node.depth * 1.25 + 0.25}rem` }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(id)}
                    disabled={isDisabled}
                    onChange={() => toggle(id)}
                  />
                  <span className="text-slate-900">{node.account.name}</span>
                  <span className="text-xs text-slate-400">
                    ({ACCOUNT_TYPE_LABEL[node.account.accountType]})
                  </span>
                </label>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
